// src/components/Dashboard/DashboardComponents/TrendingConversations.jsx
// UPGRADED VERSION - CLEAR COMMUNITY SIGNALS
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, TrendingUp, Activity, Star, Eye, MessageSquare, AlertCircle } from 'lucide-react';

/**
 * Calculate visibility/attention score for trending stocks
 */
const calculateAttentionScore = (stock) => {
  const mentionScore = Math.min(50, (stock.mentions || 0) * 2);
  const credScore = Math.min(50, (stock.credScore || 0) / 2);
  return Math.round(mentionScore + credScore);
};

/**
 * Get contextual description for sentiment score
 */
const getSentimentContext = (sentiment, t) => {
  if (sentiment >= 70) return t('dashboard.trendingConversations.sentimentNotablyBullish');
  if (sentiment >= 55) return t('dashboard.trendingConversations.sentimentLeansBullish');
  if (sentiment >= 45) return t('dashboard.trendingConversations.sentimentMixed');
  if (sentiment >= 30) return t('dashboard.trendingConversations.sentimentLeansBearish');
  return t('dashboard.trendingConversations.sentimentNotablyBearish');
};

/**
 * Get contextual description for price change
 */
const getPriceContext = (change, t) => {
  const abs = Math.abs(change);
  if (abs < 1) return t('dashboard.trendingConversations.priceStable');
  if (abs < 3) return change >= 0 ? t('dashboard.trendingConversations.priceModestGain') : t('dashboard.trendingConversations.priceModestDecline');
  if (abs < 5) return change >= 0 ? t('dashboard.trendingConversations.priceNotableGain') : t('dashboard.trendingConversations.priceNotableDecline');
  return change >= 0 ? t('dashboard.trendingConversations.priceSignificantGain') : t('dashboard.trendingConversations.priceSignificantDecline');
};

const TrendingConversationsPanel = ({
  trendingConversations = [],
  trendingTab = "credible",
  setTrendingTab = () => {},
  watchlistTickersSet = new Set(),
}) => {
  const { t } = useTranslation();
  const sorted = trendingConversations
    .slice()
    .sort((a, b) =>
      trendingTab === 'credible'
        ? (b?.credScore ?? 0) - (a?.credScore ?? 0)
        : (b?.mentions ?? 0) - (a?.mentions ?? 0)
    );

  return (
    <div className="bg-cream-100 dark:bg-slate-900/60 border border-cream-300 dark:border-slate-800/60 rounded-xl overflow-hidden">

      {/* HEADER */}
      <div className="p-4 border-b border-cream-300 dark:border-slate-800/60">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-5 h-5 text-orange-600 dark:text-orange-400" />
          <h2 className="text-base font-bold text-gray-900 dark:text-slate-200">
            {t('dashboard.trendingConversations.title')}
          </h2>
        </div>

        {/* TABS */}
        <div className="flex gap-2">
          <button
            onClick={() => setTrendingTab('credible')}
            className={`flex-1 py-2 text-xs font-medium rounded-lg transition-colors ${
              trendingTab === 'credible'
                ? 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/30'
                : 'text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200 border border-transparent hover:border-gray-300 dark:hover:border-slate-700'
            }`}
          >
            <div className="flex items-center justify-center gap-1">
              <Star className="w-3 h-3" />
              {t('dashboard.trendingConversations.tabAlignmentLabel')}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-slate-500 mt-0.5">
              {t('dashboard.trendingConversations.tabAlignmentHint')}
            </div>
          </button>
          <button
            onClick={() => setTrendingTab('volume')}
            className={`flex-1 py-2 text-xs font-medium rounded-lg transition-colors ${
              trendingTab === 'volume'
                ? 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border border-orange-500/30'
                : 'text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200 border border-transparent hover:border-gray-300 dark:hover:border-slate-700'
            }`}
          >
            <div className="flex items-center justify-center gap-1">
              <MessageSquare className="w-3 h-3" />
              {t('dashboard.trendingConversations.tabVolumeLabel')}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-slate-500 mt-0.5">
              {t('dashboard.trendingConversations.tabVolumeHint')}
            </div>
          </button>
        </div>
      </div>

      {/* LIST */}
      <div className="divide-y divide-cream-300 dark:divide-slate-800/40">
        {sorted.map((stock) => {
          const isWatchlist = watchlistTickersSet.has(stock.ticker);
          const attentionScore = calculateAttentionScore(stock);
          const sentimentContext = getSentimentContext(stock.sentiment, t);
          const priceContext = getPriceContext(stock.change, t);
          
          // Detect unusual conditions
          const hasUnusualVolume = stock.volume_ratio && stock.volume_ratio > 2.0;
          const hasHighMentions = stock.mentions > 50;
          const isUnusual = hasUnusualVolume || hasHighMentions;

          return (
            <div
              key={stock.ticker}
              className={`p-4 hover:bg-cream-900/5 dark:hover:bg-slate-800/30 transition-colors cursor-pointer ${
                isWatchlist ? 'border-l-2 border-l-blue-400 bg-blue-500/5' : ''
              } ${isUnusual ? 'bg-amber-500/5' : ''}`}
            >
              {/* UNUSUAL BADGE */}
              {isUnusual && (
                <div className="mb-2">
                  <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20 rounded flex items-center gap-1 w-fit">
                    <AlertCircle className="w-3 h-3" />
                    {hasHighMentions && t('dashboard.trendingConversations.highDiscussionVolume')}
                    {hasUnusualVolume && !hasHighMentions && t('dashboard.trendingConversations.unusualTradingVolume')}
                  </span>
                </div>
              )}

              {/* TOP ROW -- stock.name was previously unguarded plain text in
                  this flex row with no truncate/min-w-0, a real overflow
                  risk with a long company name (found live during the
                  2026-07-17 mobile audit); matches the truncate convention
                  already established in StockSelector.jsx. */}
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-bold text-base text-gray-900 dark:text-slate-100 flex items-center gap-2">
                    {stock.ticker}
                    {isWatchlist && (
                      <Star className="w-3 h-3 text-blue-600 fill-blue-600 dark:text-blue-300 dark:fill-blue-300 flex-shrink-0" />
                    )}
                  </div>
                  <div className="text-[11px] text-gray-500 dark:text-slate-500 truncate max-w-[160px]" title={stock.name}>
                    {stock.name}
                  </div>
                </div>

                <div className="text-right flex-shrink-0">
                  <div className="text-lg font-semibold text-gray-900 dark:text-slate-200">
                    ${Number(stock.price).toFixed(2)}
                  </div>
                  <div
                    className={`text-xs font-medium ${
                      stock.change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {stock.change >= 0 ? '+' : ''}
                    {Number(stock.change).toFixed(2)}%
                  </div>
                </div>
              </div>

              {/* PRICE CONTEXT */}
              <div className="text-xs text-gray-500 dark:text-slate-500 mb-3">
                {t('dashboard.trendingConversations.priceContextOverPeriod', { context: priceContext })}
              </div>

              {/* ATTENTION + SENTIMENT -- combined into one compact row
                  (was two separate full-width blocks, each with its own
                  label/value line and a captioned explanation line -- see
                  2026-07-17 dashboard density cleanup). */}
              <div className="flex items-center gap-4 mb-3 text-[11px]">
                <div className="flex items-center gap-1.5 flex-1 min-w-0" title={t('dashboard.trendingConversations.communityAttentionTitle', { score: attentionScore })}>
                  <Eye className="w-3 h-3 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                  <div className="flex-1 h-1.5 bg-gray-200 dark:bg-slate-800/60 rounded-full overflow-hidden min-w-0">
                    <div
                      className={`h-full ${
                        attentionScore >= 70 ? 'bg-orange-500' : attentionScore >= 40 ? 'bg-blue-500' : 'bg-slate-500'
                      }`}
                      style={{ width: `${attentionScore}%` }}
                    />
                  </div>
                  <span className="text-gray-500 dark:text-slate-400 flex-shrink-0">{attentionScore}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-1 min-w-0" title={sentimentContext}>
                  <span className="text-gray-500 dark:text-slate-500 flex-shrink-0">{t('dashboard.trendingConversations.sentAbbrev')}</span>
                  <div className="flex-1 h-1.5 bg-gray-200 dark:bg-slate-800/60 rounded-full overflow-hidden min-w-0">
                    <div
                      className={`h-full ${
                        stock.sentiment >= 65 ? 'bg-green-500' : stock.sentiment >= 45 ? 'bg-blue-500' : 'bg-orange-500'
                      }`}
                      style={{ width: `${stock.sentiment}%` }}
                    />
                  </div>
                  <span className="text-gray-500 dark:text-slate-400 flex-shrink-0">{Math.round(stock.sentiment)}</span>
                </div>
              </div>

              {/* FOOTER METRICS */}
              <div className="flex items-center justify-between text-xs flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 dark:text-slate-400 flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" />
                    {stock.mentions} {stock.mentions === 1 ? t('dashboard.trendingConversations.mention') : t('dashboard.trendingConversations.mentions')}
                  </span>

                  {stock.volume_ratio && (
                    <span className="text-gray-500 dark:text-slate-400">
                      {t('dashboard.trendingConversations.volAvg', { ratio: stock.volume_ratio.toFixed(1) })}
                    </span>
                  )}
                </div>

                <span
                  className={`px-2 py-1 rounded border text-[10px] font-medium ${
                    trendingTab === 'credible'
                      ? 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20'
                      : 'bg-orange-100 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/20'
                  }`}
                >
                  {trendingTab === 'credible'
                    ? t('dashboard.trendingConversations.histAlignment', { score: Math.round(stock.credScore) })
                    : t('dashboard.trendingConversations.highRecentActivity')}
                </span>
              </div>

              {/* WATCHLIST TAG */}
              {isWatchlist && (
                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-slate-700/40">
                  <div className="text-[10px] text-blue-600 dark:text-blue-300 flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    {t('dashboard.trendingConversations.onYourWatchlist')}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* EMPTY STATE */}
      {sorted.length === 0 && (
        <div className="p-8 text-center">
          <Activity className="w-12 h-12 text-gray-400 dark:text-slate-600 mx-auto mb-3 opacity-50" />
          <p className="text-gray-500 dark:text-slate-400 text-sm">
            {t('dashboard.trendingConversations.noTrendingDiscussions')}
          </p>
        </div>
      )}

    </div>
  );
};

export default TrendingConversationsPanel;